// src/pages/Wallet.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
    Loader2,
    Wallet as WalletIcon,
    Plus,
    CreditCard,
    TrendingUp,
    TrendingDown,
    Filter,
    Search,
    ChevronRight,
    ChevronLeft,
} from 'lucide-react';
import { walletAPI } from '../services/api';
import { Wallet as WalletType, Transaction } from '../types';
import { toast } from 'sonner';

/* ========= Helpers ========= */
const fa = new Intl.NumberFormat('fa-IR');
const faDate = (d?: string | number | Date | null) =>
    d ? new Date(d).toLocaleDateString('fa-IR') : '—';

const isCreditType = (t?: string) => {
    const v = String(t || '').toLowerCase();
    return ['credit', 'charge', 'commission'].includes(v);
};

const typeLabel = (t?: string) => {
    const v = String(t || '').toLowerCase();
    if (v === 'credit' || v === 'charge') return 'واریز';
    if (v === 'commission') return 'کمیسیون';
    if (v === 'debit' || v === 'purchase') return 'برداشت';
    return 'تراکنش';
};

const typeIcon = (t?: string) => {
    const v = String(t || '').toLowerCase();
    if (v === 'credit' || v === 'charge') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (v === 'commission') return <TrendingUp className="h-4 w-4 text-emerald-600" />;
    if (v === 'debit' || v === 'purchase') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <CreditCard className="h-4 w-4 text-gray-400" />;
};

const statusBadgeVariant = (status?: string) => {
    switch (String(status || '').toLowerCase()) {
        case 'completed':
        case 'success':
        case 'paid':
            return 'default';
        case 'failed':
        case 'canceled':
        case 'cancelled':
            return 'destructive';
        default:
            return 'outline';
    }
};

const parseISODateOnly = (s?: string) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

/* ========= Component ========= */
const Wallet: React.FC = () => {
    const [wallet, setWallet] = useState<WalletType | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // شارژ
    const [dialogOpen, setDialogOpen] = useState(false);
    const [chargeAmount, setChargeAmount] = useState('');
    const [chargeLoading, setChargeLoading] = useState(false);

    // فیلترها
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [type, setType] = useState<'all' | 'credit' | 'commission' | 'debit'>('all');
    const [status, setStatus] = useState<'all' | 'success' | 'failed' | 'pending' | 'paid'>('all');
    const [from, setFrom] = useState<string>('');
    const [to, setTo] = useState<string>('');

    // پجینیشن
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchWalletData();
    }, []);

    const fetchWalletData = async () => {
        try {
            // ✅ تغییر نام متد از getWalletInfo به getWalletSummary
            const [walletData, transactionsData] = await Promise.all([
                walletAPI.getWalletSummary(),
                walletAPI.listTransactions(1, 100).then(r => r.items || []),
            ]);
            setWallet(walletData);
            setTransactions(transactionsData);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || err?.message || 'خطا در دریافت اطلاعات کیف‌پول';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleChargeWallet = async () => {
        const amountNum = Number(chargeAmount) * 10;
        if (!amountNum || amountNum <= 0) {
            toast.error('لطفاً مبلغ معتبر وارد کنید');
            return;
        }
        setChargeLoading(true);
        try {
            const response = await walletAPI.chargeWallet(amountNum);
            if (response?.url) {
                window.location.href = response.url;
            } else {
                toast.success('درخواست شارژ با موفقیت ثبت شد');
                setDialogOpen(false);
                setChargeAmount('');
                await fetchWalletData();
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'شارژ کیف‌پول ناموفق بود';
            toast.error(msg);
        } finally {
            setChargeLoading(false);
        }
    };

    /* ---------- فیلتر سمت کلاینت ---------- */
    const filtered = useMemo(() => {
        const q = search.trim();
        const fromDate = from ? new Date(from + 'T00:00:00') : null;
        const toDate = to ? new Date(to + 'T23:59:59') : null;

        return transactions.filter((t: any) => {
            const tType = String(t.type || '').toLowerCase();
            const tStatus = String(t.status || '').toLowerCase();
            const amt = Number(t.amount ?? 0);
            const txt = [t.description, t.reference, tType, tStatus].filter(Boolean).join(' ').toLowerCase();

            const matchType =
                type === 'all' ||
                (type === 'credit' && (tType === 'credit' || tType === 'charge')) ||
                (type === 'commission' && tType === 'commission') ||
                (type === 'debit' && (tType === 'debit' || tType === 'purchase'));

            const matchStatus =
                status === 'all' ||
                (status === 'success' && (tStatus === 'success' || tStatus === 'completed' || tStatus === 'paid')) ||
                (status === 'failed' && (tStatus === 'failed' || tStatus === 'canceled' || tStatus === 'cancelled')) ||
                (status === 'pending' && tStatus === 'pending') ||
                (status === 'paid' && tStatus === 'paid');

            const matchText = !q || txt.includes(q.toLowerCase()) || String(amt).includes(q);

            const created = parseISODateOnly(t.created_at);
            const matchFrom = !fromDate || (created && created >= fromDate);
            const matchTo = !toDate || (created && created <= toDate);

            return matchType && matchStatus && matchText && matchFrom && matchTo;
        });
    }, [transactions, search, type, status, from, to]);

    // مرتب‌سازی (جدیدترین اول)
    const sorted = useMemo(
        () =>
            [...filtered].sort((a: any, b: any) => {
                const da = new Date(a.created_at).getTime();
                const db = new Date(b.created_at).getTime();
                return db - da;
            }),
        [filtered]
    );

    /* ---------- صفحه‌بندی ---------- */
    const totalItems = sorted.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);
    const paged = useMemo(() => {
        const start = (page - 1) * pageSize;
        return sorted.slice(start, start + pageSize);
    }, [sorted, page, pageSize]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-green-600 mx-auto mb-3" />
                    <p className="text-green-700 font-medium">در حال بارگذاری کیف‌پول...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4" dir="rtl">
                <Alert variant="destructive" className="max-w-xl w-full">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    const balance = Number((wallet as any)?.balance ?? 0);
    const totalCredit = transactions
        .filter((t: any) => isCreditType(t.type))
        .reduce((sum, t: any) => sum + Number(t.amount ?? 0), 0);
    const totalDebit = transactions
        .filter((t: any) => !isCreditType(t.type))
        .reduce((sum, t: any) => sum + Number(t.amount ?? 0), 0);

    return (
        <div
            className="space-y-6 p-4 md:p-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 min-h-screen"
            dir="rtl"
        >
            {/* هدر گرادیانی + خلاصه */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-6 md:p-8 shadow-2xl">
                <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_top,_#ffffff33,_transparent_55%),radial-gradient(circle_at_bottom,_#00000022,_transparent_55%)]" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="space-y-12 text-white">
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center border border-white/25">
                                <WalletIcon className="h-6 w-6 text-emerald-50" />
                            </div>
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold">کیف‌پول شما</h1>
                                <p className="text-sm md:text-base text-emerald-100">
                                    مدیریت موجودی، شارژ کیف‌پول و مشاهدهٔ تاریخچه تراکنش‌ها
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-xs border border-white/25">
                                <div className="text-emerald-50">کل واریزها</div>
                                <div className="text-lg font-bold">{fa.format(totalCredit / 10)} تومان</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-xs border border-white/25">
                                <div className="text-emerald-50">کل برداشت‌ها</div>
                                <div className="text-lg font-bold">{fa.format(totalDebit / 10)} تومان</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-xs border border-white/25 hidden sm:block">
                                <div className="text-emerald-50">تعداد تراکنش</div>
                                <div className="text-lg font-bold">{fa.format(transactions.length)}</div>
                            </div>
                        </div>
                    </div>

                    {/* اکشن‌ها: فیلتر + شارژ */}
                    <div className="flex flex-col text-white gap-3 space-y-4 items-stretch md:items-start">
                        <div className="mt-3 w-full">
                            <span className="text-xl text-emerald-50">موجودی</span>
                            <div className="text-3xl md:text-4xl text-end mt-1">
                                {fa.format(balance / 10)} <span className="text-lg font-semibold">تومان</span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button className="bg-white text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto">
                                        <Plus className="ml-2 h-4 w-4" />
                                        شارژ کیف‌پول
                                    </Button>
                                </DialogTrigger>

                                <DialogContent dir="rtl">
                                    <DialogHeader>
                                        <DialogTitle>شارژ کیف‌پول</DialogTitle>
                                        <DialogDescription>
                                            مبلغ موردنظر برای شارژ را وارد کنید (تومان)
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="amount">مبلغ (تومان)</Label>
                                            <Input
                                                id="amount"
                                                type="number"
                                                placeholder="مثلاً ۵۰۰۰۰"
                                                value={chargeAmount}
                                                onChange={(e) => setChargeAmount(e.target.value)}
                                                dir="ltr"
                                            />
                                        </div>
                                        <Button
                                            onClick={handleChargeWallet}
                                            disabled={chargeLoading}
                                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                                        >
                                            {chargeLoading ? (
                                                <>
                                                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                                    در حال پردازش…
                                                </>
                                            ) : (
                                                'رفتن به درگاه و شارژ'
                                            )}
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                            <Button
                                variant="outline"
                                className="border-emerald-200 bg-white/10 text-emerald-50 hover:bg-white/20 w-full sm:w-auto"
                                onClick={() => setFiltersOpen((v) => !v)}
                            >
                                <Filter className="ml-2 h-4 w-4" />
                                {filtersOpen ? 'بستن فیلترها' : 'فیلتر تراکنش‌ها'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* فیلترها */}
            {filtersOpen && (
                <Card className="border-green-200/60 bg-white/90 backdrop-blur-sm shadow-md">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-green-900 text-base">فیلتر تراکنش‌ها</CardTitle>
                        <CardDescription className="text-green-700">
                            جستجو، نوع/وضعیت و بازهٔ زمانی
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="space-y-1 md:col-span-2">
                                <Label className="text-sm">جستجو (توضیح/نوع/وضعیت/مبلغ)</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        placeholder="مثال: واریز / 100000 / کمیسیون"
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(1);
                                        }}
                                        className="pl-10 border-green-200 focus:ring-2 focus:ring-green-600"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-sm">نوع</Label>
                                <select
                                    value={type}
                                    onChange={(e) => {
                                        setType(e.target.value as any);
                                        setPage(1);
                                    }}
                                    className="h-10 rounded-md border border-green-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                                >
                                    <option value="all">همه</option>
                                    <option value="credit">واریز</option>
                                    <option value="commission">کمیسیون</option>
                                    <option value="debit">برداشت</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-sm">وضعیت</Label>
                                <select
                                    value={status}
                                    onChange={(e) => {
                                        setStatus(e.target.value as any);
                                        setPage(1);
                                    }}
                                    className="h-10 rounded-md border border-green-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                                >
                                    <option value="all">همه</option>
                                    <option value="success">موفق</option>
                                    <option value="paid">پرداخت‌شده</option>
                                    <option value="pending">در انتظار</option>
                                    <option value="failed">ناموفق</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-sm">از تاریخ</Label>
                                <Input
                                    type="date"
                                    value={from}
                                    onChange={(e) => {
                                        setFrom(e.target.value);
                                        setPage(1);
                                    }}
                                    className="border-green-200 focus:ring-2 focus:ring-green-600"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-sm">تا تاریخ</Label>
                                <Input
                                    type="date"
                                    value={to}
                                    onChange={(e) => {
                                        setTo(e.target.value);
                                        setPage(1);
                                    }}
                                    className="border-green-200 focus:ring-2 focus:ring-green-600"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => {
                                    setSearch('');
                                    setType('all');
                                    setStatus('all');
                                    setFrom('');
                                    setTo('');
                                    setPage(1);
                                }}
                            >
                                پاک‌سازی فیلترها
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* لیست تراکنش‌ها */}
            <Card className="border-emerald-600/60 bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="text-right">
                    <CardTitle className="text-green-900">تاریخچه تراکنش‌ها</CardTitle>
                    <CardDescription className="text-green-700">
                        {fa.format(totalItems)} تراکنش مطابق فیلترها یافت شد
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* موبایل: کارت‌ویو */}
                    <div className="grid gap-3 sm:hidden">
                        {paged.length === 0 ? (
                            <div className="text-center py-8">
                                <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                <p className="text-gray-500">تراکنشی یافت نشد</p>
                            </div>
                        ) : (
                            paged.map((t: any) => {
                                const credit = isCreditType(t.type);
                                const amt = Number(t.amount ?? 0);
                                return (
                                    <div
                                        key={t.id}
                                        className="rounded-xl border border-emerald-600/60 p-3 bg-white shadow-sm"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="font-semibold text-green-900">
                                                {typeLabel(t.type)}
                                            </div>
                                            <Badge variant={statusBadgeVariant(t.status || 'success')}>
                                                {t.status || 'موفق'}
                                            </Badge>
                                        </div>
                                        <div className="mt-2 text-sm text-green-800 space-y-1">
                                            <div className="flex items-center justify-between">
                                                <span className={credit ? 'text-green-700' : 'text-red-600'}>
                                                    {credit ? '+' : '-'} {fa.format(Math.abs(amt / 10))} تومان
                                                </span>
                                                {typeIcon(t.type)}
                                            </div>
                                            <div className="text-gray-600">{t.description || '—'}</div>
                                            <div className="text-green-700">
                                                تاریخ: {faDate(t.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* دسکتاپ: جدول */}
                    <div className="hidden sm:block overflow-x-auto">
                        {paged.length === 0 ? (
                            <div className="text-center py-8">
                                <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                <p className="text-gray-500">تراکنشی یافت نشد</p>
                            </div>
                        ) : (
                            <Table className="min-w-[720px]">
                                <TableHeader>
                                    <TableRow className="bg-green-50">
                                        <TableHead className="text-right text-green-800">نوع</TableHead>
                                        <TableHead className="text-right text-green-800">توضیحات</TableHead>
                                        <TableHead className="text-right text-green-800">مبلغ</TableHead>
                                        <TableHead className="text-right text-green-800">وضعیت</TableHead>
                                        <TableHead className="text-right text-green-800">تاریخ</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paged.map((t: any) => {
                                        const credit = isCreditType(t.type);
                                        const amt = Number(t.amount ?? 0);
                                        return (
                                            <TableRow key={t.id} className="hover:bg-green-50/40">
                                                <TableCell>
                                                    <div className="flex items-center justify-end gap-2">
                                                        <span className="capitalize">
                                                            {typeLabel(t.type)}
                                                        </span>
                                                        {typeIcon(t.type)}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {t.description || '—'}
                                                </TableCell>
                                                <TableCell
                                                    className={`font-semibold text-right ${
                                                        credit ? 'text-green-600' : 'text-red-600'
                                                    }`}
                                                    dir="ltr"
                                                >
                                                    {credit ? '+' : '-'}{' '}
                                                    {fa.format(Math.abs(amt / 10))} تومان
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Badge variant={statusBadgeVariant(t.status || 'success')}>
                                                        {t.status || 'موفق'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {faDate(t.created_at)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    {/* پجینیشن */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">آیتم در هر صفحه:</span>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                className="h-9 rounded-md border border-green-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                            >
                                <option value={5}>۵</option>
                                <option value={10}>۱۰</option>
                                <option value={20}>۲۰</option>
                                <option value={50}>۵۰</option>
                            </select>
                        </div>

                        <div className="text-sm text-gray-600">
                            صفحه {fa.format(page)} از {fa.format(totalPages)} — مجموع{' '}
                            {fa.format(totalItems)} تراکنش
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                            >
                                <ChevronRight className="ml-1 h-4 w-4" />
                                قبلی
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                            >
                                بعدی
                                <ChevronLeft className="mr-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Wallet;
